import { useCallback, useEffect, useState } from 'react';
import {
  createConnection,
  deleteConnection,
  fetchConnections,
  fetchProviders,
  syncConnection,
} from '../api.js';
import { relativeTime } from '../../../shared/time.js';
// Imported rather than referenced by path, so Vite fingerprints it and it
// ships with the bundle instead of having to exist at a URL the deploy
// happens to serve.
import goodreadsIcon from '../../../assets/goodreads.svg';
import { useToast } from '../Toaster.jsx';

// Per-provider connection forms. Config fields are non-secret and land on the
// connection; secret fields go to the secret store and are never read back,
// which is why they are always blank on load.
const PROVIDER_FORMS = {
  goodreads: {
    label: 'Goodreads',
    hint: 'Shelves are public, so no credentials are needed. The RSS link from a shelf page works too.',
    // Goodreads never shows a bare user ID, so ask for the URL people actually
    // have. The source parses the ID out of it server-side.
    config: [{
      name: 'userId',
      label: 'Goodreads profile or shelf URL',
      placeholder: 'https://www.goodreads.com/user/show/161805133-bradley-hughes',
    }],
    secrets: [],
    // What the connection points at, rebuilt from the id the source parsed
    // out of whatever was originally pasted. Worth showing because that id is
    // all the connection stores, and a bare number is not something anyone can
    // check at a glance.
    link: {
      label: 'Goodreads profile',
      url: (config) => (config?.userId ? `https://www.goodreads.com/user/show/${config.userId}` : ''),
    },
  },
};

// A provider's own mark, which is what people recognise a row by before they
// have read a word of it. Providers with no icon fall back to their initial
// rather than to a gap, so the rows stay aligned.
const PROVIDER_ICONS = {
  goodreads: goodreadsIcon,
};

// What a connection's two numbers mean, per module. The API returns them
// unnamed, because what a group is depends on the content: shelves for books,
// and whatever albums and lists turn out to be for the modules still to come.
const STAT_LABELS = {
  bookshelf: { items: ['book', 'books'], groups: ['shelf', 'shelves'] },
  photos: { items: ['photo', 'photos'], groups: ['album', 'albums'] },
  todos: { items: ['task', 'tasks'], groups: ['list', 'lists'] },
};

function count(value, [singular, plural]) {
  return `${value} ${value === 1 ? singular : plural}`;
}

// The counts a connection has to show for itself. Nothing is printed before a
// first sync, because "0 books" reads like a failure where it only means the
// schedule has not come round yet.
function statsLine(connection) {
  const labels = STAT_LABELS[connection.module];
  const stats = connection.stats;
  if (!labels || !stats || !stats.items) return '';

  const parts = [count(stats.items, labels.items)];
  if (stats.groups) parts.push(count(stats.groups, labels.groups));
  return parts.join(' · ');
}

function ProviderIcon({ provider }) {
  const src = PROVIDER_ICONS[provider];
  if (!src) {
    return (
      <span className="provider-icon provider-icon-fallback" aria-hidden="true">
        {(provider || '?').charAt(0).toUpperCase()}
      </span>
    );
  }
  // Empty alt: the provider is already named in the row beside it, so a
  // screen reader announcing it twice would only be noise.
  return <img className="provider-icon" src={src} alt="" />;
}

// One row's drawer: what is worth having but not worth carrying in a list.
// Remove lives here rather than in the header because it is irreversible and
// sits a mouse-slip away from Sync, which is the opposite.
function ConnectionDrawer({ connection, busy, onDelete }) {
  const [copied, setCopied] = useState(false);
  const link = PROVIDER_FORMS[connection.provider]?.link;
  const url = link?.url(connection.config) || '';

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the link is selectable either way.
      setCopied(false);
    }
  }

  return (
    <div className="connection-drawer">
      {url && (
        <div className="drawer-field">
          <span className="drawer-label">{link.label}</span>
          <div className="share-link">
            <a href={url} target="_blank" rel="noreferrer" className="drawer-url">
              {url}
            </a>
            <button type="button" onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className="drawer-actions">
        <button
          type="button"
          className="danger"
          onClick={() => onDelete(connection.id)}
          disabled={busy === connection.id}
        >
          Remove
        </button>
        <span className="drawer-hint">
          Removes the connection and everything it synced. The source itself is untouched.
        </span>
      </div>
    </div>
  );
}

// How often to look, and how long to keep looking, after a sync is queued.
const SYNC_POLL_MS = 3000;
const SYNC_WATCH_MS = 90000;

// How recently a connection must have synced to count as healthy. The
// bookshelf cadence is a day, so anything older than that has missed a run.
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

// syncHealth is what the badge says: whether the last sync worked, and whether
// it was recent enough to trust. A connection that has never synced is not
// healthy either - there is nothing behind it yet.
function syncHealth(connection) {
  if (connection.lastError) {
    return { ok: false, label: `Last sync failed: ${connection.lastError}` };
  }
  if (!connection.lastSyncedAt) {
    return { ok: false, label: 'Never synced' };
  }

  const age = Date.now() - new Date(connection.lastSyncedAt).getTime();
  if (Number.isNaN(age) || age > STALE_AFTER_MS) {
    return { ok: false, label: `No successful sync in the last 24 hours` };
  }
  return { ok: true, label: `Synced ${relativeTime(connection.lastSyncedAt)}` };
}

// A circle rather than a bare glyph, so the two states differ in shape as well
// as in colour - red and green are the pair most often indistinguishable.
function HealthBadge({ connection }) {
  const { ok, label } = syncHealth(connection);
  return (
    <span className={`health-badge ${ok ? 'ok' : 'bad'}`} role="img" aria-label={label} title={label}>
      {ok ? '✓' : '✕'}
    </span>
  );
}

// An inline SVG rather than a character, because it has to turn: a glyph would
// rotate around whatever box its font gave it and wobble.
function SyncSpinner() {
  return (
    <svg className="sync-spinner" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path
        d="M8 2.5A5.5 5.5 0 1 0 13.5 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function ConnectionsPanel() {
  const [connections, setConnections] = useState([]);
  // Expanded rows, by id. A set rather than a single id, so opening one does
  // not shut another somebody was reading.
  const [expanded, setExpanded] = useState(() => new Set());
  // Rows with a sync in flight. Separate from `busy`, which covers a request
  // this page is waiting on: a queued sync is finished here long before it is
  // finished, and the spinner has to follow the work rather than the request.
  const [syncing, setSyncing] = useState(() => new Set());
  const [providers, setProviders] = useState([]);
  const [draft, setDraft] = useState({ provider: '', displayName: '', values: {} });
  const notify = useToast();
  const [busy, setBusy] = useState('');

  const reload = useCallback(() => {
    fetchConnections()
      .then(setConnections)
      .catch((error) => console.error('Failed to load connections', error));
  }, []);

  useEffect(() => {
    fetchProviders()
      .then((list) => {
        setProviders(list);
        setDraft((current) => ({ ...current, provider: current.provider || list[0] || '' }));
      })
      .catch((error) => console.error('Failed to load providers', error));
    reload();
  }, [reload]);

  async function handleCreate(event) {
    event.preventDefault();
    function toggle(id) {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  const spec = PROVIDER_FORMS[draft.provider];
    if (!spec) return;

    const config = {};
    const secrets = {};
    for (const field of spec.config) config[field.name] = draft.values[field.name] || '';
    for (const field of spec.secrets) secrets[field.name] = draft.values[field.name] || '';

    setBusy('create');
    try {
      const created = await createConnection({
        provider: draft.provider,
        displayName: draft.displayName || spec.label,
        config,
        ...(spec.secrets.length ? { secrets } : {}),
      });
      notify(`Connected ${created.connection.displayName}. Syncing…`);
      setDraft({ provider: draft.provider, displayName: '', values: {} });

      // Sync straight away so displays fill without waiting for the schedule.
      const result = await syncConnection(created.connection.id);
      notify(result.queued
        ? 'Connected. The first sync is running now and books will appear as it finishes.'
        : `Connected and synced ${result.items} items.`);
      if (result.queued) watchForSync(created.connection.id);
    } catch (error) {
      notify(`Failed: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
      reload();
    }
  }

  // A queued sync finishes somewhere else, so the row is reloaded until it
  // changes rather than left showing what it said before the button was
  // pressed. It gives up after a while: a large first sync fills in over
  // several runs, and a page that polled until it was finished would poll for
  // minutes to say nothing new.
  // A queued sync finishes somewhere else, so the row is reloaded until it
  // changes rather than left showing what it said before the button was
  // pressed. The row keeps spinning for exactly as long as this runs, which is
  // why it clears the spinner on every exit rather than the caller doing it.
  // It gives up after a while: a large first sync fills in over several runs,
  // and a page that spun until it was finished would spin for minutes.
  const watchForSync = useCallback((id, before) => {
    const started = Date.now();

    const timer = setInterval(async () => {
      const stop = () => {
        clearInterval(timer);
        setSyncing((current) => {
          const next = new Set(current);
          next.delete(id);
          return next;
        });
      };

      try {
        const latest = await fetchConnections();
        setConnections(latest);

        const connection = latest.find((candidate) => candidate.id === id);
        const landed = connection && (connection.lastSyncedAt !== before || connection.lastError);
        if (landed || Date.now() - started > SYNC_WATCH_MS) {
          stop();
          if (connection?.lastError) notify(`Sync failed: ${connection.lastError}`, { tone: 'error' });
          else if (landed) notify(`Synced ${statsLine(connection) || 'the connection'}.`);
          else notify('Still syncing. This one is large, so it finishes over several runs.');
        }
      } catch (error) {
        stop();
        console.error('Failed to check on a sync', error);
      }
    }, SYNC_POLL_MS);
  }, []);

  async function handleSync(id) {
    const before = connections.find((candidate) => candidate.id === id)?.lastSyncedAt || '';
    setSyncing((current) => new Set(current).add(id));

    try {
      const result = await syncConnection(id);
      if (result.queued) {
        notify('Sync started.');
        // Left spinning; watchForSync clears it when the sync lands.
        watchForSync(id, before);
        return;
      }
      notify(`Synced ${result.items} items.`);
    } catch (error) {
      notify(`Sync failed: ${error.message}`, { tone: 'error' });
    }

    // Only the inline path reaches here, having already finished.
    setSyncing((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    reload();
  }

  async function handleDelete(id) {
    setBusy(id);
    try {
      await deleteConnection(id);
      notify('Connection removed.');
    } catch (error) {
      notify(`Delete failed: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
      reload();
    }
  }

  function toggle(id) {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  const spec = PROVIDER_FORMS[draft.provider];

  return (
    <>
      <section className="panel">
        {connections.length === 0 ? (
          <p className="panel-hint">No connections yet.</p>
        ) : (
          <ul className="connection-list">
            {connections.map((connection) => (
              <li key={connection.id} className="connection-row">
                <div className="connection-header">
                  {/* The summary is the toggle. The status and Sync sit
                      outside it, because a button cannot contain buttons. */}
                  <button
                    type="button"
                    className="connection-toggle"
                    aria-expanded={expanded.has(connection.id)}
                    aria-controls={`connection-drawer-${connection.id}`}
                    onClick={() => toggle(connection.id)}
                  >
                    <span className="disclosure" aria-hidden="true">
                      {expanded.has(connection.id) ? '▾' : '▸'}
                    </span>
                    <ProviderIcon provider={connection.provider} />
                    <span className="connection-detail">
                      <strong>{connection.displayName || connection.provider}</strong>
                      <span className="author">
                        {connection.module}
                        {connection.lastSyncedAt ? (
                          <>
                            {' · synced '}
                            <time dateTime={connection.lastSyncedAt} title={connection.lastSyncedAt}>
                              {relativeTime(connection.lastSyncedAt)}
                            </time>
                          </>
                        ) : ' · never synced'}
                      </span>
                      {statsLine(connection) && (
                        <span className="connection-stats">{statsLine(connection)}</span>
                      )}
                      {/* A failed sync stays on the face of the row. Hiding
                          the reason a connection is broken behind a click is
                          exactly the wrong thing to hide. */}
                      {connection.lastError && (
                        <span className="connection-error">{connection.lastError}</span>
                      )}
                    </span>
                  </button>

                  <div className="connection-actions">
                    <HealthBadge connection={connection} />
                    <button
                      type="button"
                      className="sync-button"
                      onClick={() => handleSync(connection.id)}
                      disabled={syncing.has(connection.id)}
                      aria-label={syncing.has(connection.id) ? 'Syncing' : 'Sync'}
                    >
                      {syncing.has(connection.id) ? <SyncSpinner /> : 'Sync'}
                    </button>
                  </div>
                </div>

                {/* Rendered either way and hidden with the attribute, so
                    aria-controls always points at something real. */}
                <div id={`connection-drawer-${connection.id}`} hidden={!expanded.has(connection.id)}>
                  <ConnectionDrawer connection={connection} busy={busy} onDelete={handleDelete} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <form className="panel" onSubmit={handleCreate}>
        <h2>Add a connection</h2>
        <label className="field">
          <span>Provider</span>
          <select
            value={draft.provider}
            onChange={(event) => setDraft({ provider: event.target.value, displayName: '', values: {} })}
          >
            {providers.map((provider) => (
              <option key={provider} value={provider}>
                {PROVIDER_FORMS[provider]?.label || provider}
              </option>
            ))}
          </select>
        </label>

        {spec ? (
          <>
            {spec.hint && <p className="panel-hint">{spec.hint}</p>}
            <label className="field">
              <span>Display name</span>
              <input
                type="text"
                value={draft.displayName}
                placeholder={spec.label}
                onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
              />
            </label>
            {[...spec.config, ...spec.secrets].map((field) => (
              <label className="field" key={field.name}>
                <span>{field.label}</span>
                <input
                  type={spec.secrets.includes(field) ? 'password' : 'text'}
                  value={draft.values[field.name] || ''}
                  placeholder={field.placeholder || ''}
                  onChange={(event) =>
                    setDraft({ ...draft, values: { ...draft.values, [field.name]: event.target.value } })
                  }
                />
              </label>
            ))}
            <button type="submit" className="save-button" disabled={busy === 'create'}>
              {busy === 'create' ? 'Connecting…' : 'Connect and sync'}
            </button>
          </>
        ) : (
          <p className="panel-hint">No form defined for this provider yet.</p>
        )}
      </form>

    </>
  );
}
